const API_BASE_URL = '/api/prisme';

export type FeedbackType =
  | 'BUG'
  | 'CONTENU'
  | 'SUGGESTION_CONCEPT'
  | 'LANGUE'
  | 'UX'
  | 'BRAINSTORM'
  | 'PROCESSUS_METHODOLOGIE'
  | 'AUTRE';

export interface FeedbackRecord {
  id: number;
  user_id: number;
  user_email: string;
  context_level: 'GENERAL' | 'DEPARTMENT' | 'CARD';
  department_key: string;
  card_id?: number | null;
  type: FeedbackType;
  priority: number; // 1-4
  expected_date?: string | null;
  promised_date?: string | null;
  comment: string;
  approved_manager: boolean;
  approved_ce: boolean;
  approved_dany: boolean;
  /** Pour la prog — commentaire (J-M / Dany) */
  prog_comment?: string | null;
  /** Pour la prog — statut: en_cours | termine */
  prog_status?: string | null;
  /** Pour la prog — date prévue ou réalisée */
  prog_date?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateFeedbackPayload {
  context_level: 'GENERAL' | 'DEPARTMENT' | 'CARD';
  department_key: string;
  card_id?: number | null;
  type: FeedbackType;
  priority: number;
  expected_date?: string | null;
  comment: string;
  approved_manager?: boolean;
}

export interface UpdateFeedbackPayload {
  type?: FeedbackType;
  priority?: number;
  expected_date?: string | null;
  promised_date?: string | null;
  comment?: string;
  approved_manager?: boolean;
  approved_ce?: boolean;
  approved_dany?: boolean;
  prog_comment?: string | null;
  prog_status?: string | null;
  prog_date?: string | null;
  department_key: string;
  card_id?: number | null;
}

export async function getFeedback(
  departmentKey: string,
  cardId?: number | null,
  options?: { includeCards?: boolean }
): Promise<FeedbackRecord[]> {
  const params = new URLSearchParams({ departmentKey });
  if (cardId !== undefined && cardId !== null) {
    params.append('cardId', String(cardId));
  }
  if (options?.includeCards) {
    params.append('includeCards', 'true');
  }

  const response = await fetch(`${API_BASE_URL}/feedback?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des commentaires');
  }
  return await response.json();
}

export async function getFeedbackForDepartmentKeys(
  departmentKeys: string[],
  options?: { includeCards?: boolean }
): Promise<FeedbackRecord[]> {
  const params = new URLSearchParams({
    departmentKeys: departmentKeys.join(','),
  });
  if (options?.includeCards) {
    params.append('includeCards', 'true');
  }

  const response = await fetch(`${API_BASE_URL}/feedback?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des commentaires');
  }
  return await response.json();
}

/** Tous les commentaires (Direction TI + PRIsME Client, ex. Bâtir Pro et autres instances). */
export async function getAllFeedback(options?: { includeCards?: boolean }): Promise<FeedbackRecord[]> {
  const params = new URLSearchParams({ all: 'true' });
  if (options?.includeCards) {
    params.append('includeCards', 'true');
  }

  const response = await fetch(`${API_BASE_URL}/feedback?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des commentaires');
  }
  return await response.json();
}

export async function createFeedback(
  payload: CreateFeedbackPayload
): Promise<FeedbackRecord> {
  const response = await fetch(`${API_BASE_URL}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erreur lors de la création du commentaire' }));
    throw new Error(error.error || 'Erreur lors de la création du commentaire');
  }

  return await response.json();
}

export async function updateFeedback(
  id: number,
  payload: UpdateFeedbackPayload
): Promise<FeedbackRecord> {
  const response = await fetch(`${API_BASE_URL}/feedback/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erreur lors de la mise à jour du commentaire' }));
    throw new Error(error.error || 'Erreur lors de la mise à jour du commentaire');
  }

  return await response.json();
}


